import { useCallback } from 'react';
import BytedUploader from '@byted/uploader';
import { checkSuccessResponse } from '@ad/ad_utils/src/utils/common';
import { get2, post2 } from '../../utils/mAxios';
import { isLocal } from '../../utils';

export const TCEX_VIDEO_UPLOAD_NAMESPACE = 'tcex_bff';
export const TCEX_VIDEO_UPLAOD_NAME = 'tcex_bff';
export const TCEX_APP_ID = 5143;

const defaultUploadUserId = TCEX_VIDEO_UPLAOD_NAME;
const defaultUploadAppId = TCEX_APP_ID;
const defaultUploadSpaceName = TCEX_VIDEO_UPLOAD_NAMESPACE;
const boeUploadSpaceName = 'openapi-bff-boei18n';
const defaultImageXServiceId = 'hdprqziq2y';
const boeImageXServiceId = 'f3ioyg2qyz';

export enum UploadTypeEnum {
  IMAGE = 'image',
  VIDEO = 'video'
}

interface UploadConfigSchema {
  type: UploadTypeEnum[]; // 选择是上传是视频还是图片
  userId?: string; // 建议设置能识别用户的唯一标识id，用于上传出错时排查问题，不要传入非 ASCII编码
  appId?: number; // 在视频点播-应用服务中创建的AppID，视频点播的质量监控等都是以这个参数来区分业务方的，务必正确填写
  imageServiceId?: string; // 在ImageX中申请的服务id
  videoSpaceName?: string; // 在视频点播中申请的点播空间名
  actions?: {
    fetchVideoUploadAuth: Function;
    publishUploadVideo: Function;
    getVideoInfo: Function;
  };
}

async function fetchVideoUploadAuth() {
  const rsp = await get2('/ccs_bff/upload/getUploadVideoAuth');
  if (checkSuccessResponse(rsp.code)) {
    const { token } = rsp.data || {};
    return token;
  }
  throw new Error('video auth failed');
}

async function publishUploadVideo(vid: string) {
  const rsp = await post2('/ccs_bff/upload/publishVideo', { vid });
  if (!checkSuccessResponse(rsp.code)) {
    // eslint-disable-next-line no-console
    console.error('video publish failed');
  }
}

export async function getVideoInfo(vid: string) {
  const rsp = await get2(
    `/ccs_bff/video/${vid}`,
    {},
    {
      showApiHint: false,
      overrideGlobalShowApiHint: true
    }
  );
  if (checkSuccessResponse(rsp.code)) {
    return rsp.data;
  }
  // eslint-disable-next-line no-console
  console.error('get video error');
}

export function generateImageUrl(options: { ImageUri: string; ImageFormat: string; region: string }) {
  const domain =
    {
      sg: 'p16-cc-sg.tiktokcdn.com',
      va: 'p16-cc-va.tiktokcdn.com'
    }[options.region] || 'p16-cc-sg.tiktokcdn.com';
  const template =
    {
      sg: 'tplv-hdprqziq2y-png',
      va: 'tplv-gejt5lnkjp-webp'
    }[options.region] || 'tplv-hdprqziq2y-png';
  return `https://${domain}/${options.ImageUri}~${template}.${options.ImageFormat}`;
}

export function generateBoeImageUrl(uri: string) {
  return `https://p-imagex-boei18n.remove.this/${uri}~tplv-f3ioyg2qyz-image.image`;
}

/**
 * will return two functions, one is onUploadFile, other one is getVideoInfo
 *
 * onUploadFile(file, uploadType) => file: to upload file uploadType: the uploader type(video/image)
 * getVideoInfo(vid) => to get the video info by vid
 */
function useUpload(config: UploadConfigSchema) {
  const { userId, appId, type, imageServiceId, videoSpaceName, actions } = config;

  // get upload initial config
  function getUploadConfig(fileName?: string) {
    let base = isLocal
      ? {
          userId: userId || defaultUploadUserId,
          appId: appId || defaultUploadAppId,
          region: 'boei18n',
          videoHost: 'https://staging-openapi-boei18n.remove.this',
          imageHost: 'https://staging-openapi-boei18n.remove.this'
        }
      : ({
          userId: userId || defaultUploadUserId,
          appId: appId || defaultUploadAppId,
          region: 'sg'
        } as any);

    if (type.includes(UploadTypeEnum.IMAGE)) {
      base = {
        ...base,
        imageConfig: {
          serviceId: imageServiceId || (isLocal ? boeImageXServiceId : defaultImageXServiceId)
        }
      };
    }

    if (type.includes(UploadTypeEnum.VIDEO)) {
      base = {
        ...base,
        videoConfig: {
          spaceName: videoSpaceName || (isLocal ? boeUploadSpaceName : defaultUploadSpaceName),
          processAction: [
            {
              name: 'GetMeta'
            },
            {
              name: 'Snapshot',
              input: {
                SnapshotTime: 1
              }
            },
            {
              name: 'AddOptionInfo',
              input: {
                Title: fileName
              }
            }
          ]
        }
      };
    }
    return base;
  }

  // create a new uploader instance
  const createUploader = useCallback(
    (fileName) => {
      const uploadConfig = getUploadConfig(fileName);
      return new BytedUploader(uploadConfig);
    },
    [userId, appId, type, imageServiceId, videoSpaceName]
  );

  const onUploadFile = useCallback(
    async (file: any, uploadType: UploadTypeEnum) => {
      const { name: fileName, file: originFile } = file;
      const auth = await (actions?.fetchVideoUploadAuth || fetchVideoUploadAuth)();
      const uploadInstance = createUploader(fileName);

      return new Promise((resolve, reject) => {
        uploadInstance.on('error', (error) => {
          reject(error);
        });

        uploadInstance.on('complete', async (result: any) => {
          if (type.includes(UploadTypeEnum.IMAGE)) {
            resolve({
              type: UploadTypeEnum.IMAGE,
              info: {
                cover: isLocal
                  ? generateBoeImageUrl(result.uploadResult.ImageUri)
                  : generateImageUrl(result.uploadResult)
              }
            });
          }

          if (type.includes(UploadTypeEnum.VIDEO)) {
            try {
              const vid = result.uploadResult.Vid as string;
              await (actions?.publishUploadVideo || publishUploadVideo)(vid);
              const res = await (actions?.getVideoInfo || getVideoInfo)(vid);
              const duration = result.uploadResult.VideoMeta.Duration;
              resolve({
                type: UploadTypeEnum.VIDEO,
                info: {
                  source: vid,
                  name: fileName,
                  type,
                  vid,
                  cover: res?.coverUrl,
                  playInfoList: res?.playInfoList,
                  duration: parseInt(duration, 10)
                }
              });
            } catch (err) {
              reject(err);
            }
          }
        });

        if (auth) {
          // eslint-disable-next-line no-console
          console.log('---upload', file, originFile);
          const fileKey = uploadInstance.addFile({
            file: originFile,
            type: uploadType,
            stsToken: {
              CurrentTime: auth.currentTime,
              ExpiredTime: auth.expiredTime,
              SessionToken: auth.sessionToken,
              AccessKeyID: auth.accessKeyId,
              SecretAccessKey: auth.secretAccessKey
            }
          } as any);
          uploadInstance.start(fileKey);
        }
      });
    },
    [createUploader, actions]
  );

  return [onUploadFile, actions?.getVideoInfo || getVideoInfo];
}

export default useUpload;
